import { Form } from "@inertiajs/react"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { RoleOption } from "@/types"

interface UserFormValues {
  id?: number
  name?: string
  email?: string
  role_id?: number
  verified?: boolean
}

interface Props {
  action: string
  method: "post" | "patch"
  roles: RoleOption[]
  user?: UserFormValues
  submitLabel: string
}

export function UserForm({ action, method, roles, user, submitLabel }: Props) {
  const persisted = Boolean(user?.id)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Account</CardTitle>
      </CardHeader>
      <CardContent>
        <Form method={method} action={action} className="space-y-4">
          {({ errors, processing }) => (
            <>
              <div className="grid gap-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="user[name]"
                  defaultValue={user?.name}
                  placeholder="Jane Doe"
                />
                <InputError messages={errors.name} />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="user[email]"
                  type="email"
                  defaultValue={user?.email}
                  placeholder="jane@example.com"
                />
                <InputError messages={errors.email} />
                {persisted && (
                  <p className="text-muted-foreground text-xs">
                    Changing the email marks the account unverified until they
                    confirm it.
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="role_id">Role</Label>
                <Select
                  name="user[role_id]"
                  defaultValue={
                    user?.role_id ? String(user.role_id) : undefined
                  }
                >
                  <SelectTrigger id="role_id">
                    <SelectValue placeholder="Choose a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem
                        key={role.id}
                        value={String(role.id)}
                        className="capitalize"
                      >
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError messages={errors.role_id} />
                <p className="text-muted-foreground text-xs">
                  The role decides what this person can do. Broker and agent
                  roles also get a profile they fill in from Settings.
                </p>
              </div>

              {persisted && (
                <div className="flex items-center gap-2">
                  <input type="hidden" name="user[verified]" value="0" />
                  <Checkbox
                    id="verified"
                    name="user[verified]"
                    value="1"
                    defaultChecked={user?.verified}
                  />
                  <Label htmlFor="verified">Email verified</Label>
                  <InputError messages={errors.verified} />
                </div>
              )}

              {!persisted && (
                <p className="text-muted-foreground text-sm">
                  No password needed — the new user gets an email to set their
                  own.
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={processing}>
                  {submitLabel}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => history.back()}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </Form>
      </CardContent>
    </Card>
  )
}
